import { useCallback } from 'react';

import { notifySuccess } from '@/portainer/services/notifications';
import { useGroups } from '@/react/portainer/environments/environment-groups/queries';

import { TypedPolicy } from '../types';
import { hasSelectedGroupsWithConflicts } from '../components/PolicyConfigurationFormSection';
import { buildDockerSecurityPayload } from '../components/docker/security/buildDockerSecurityPayload';
import { DockerSecurityFormValues } from '../components/docker/security/types';
import { buildDockerSetupPayload } from '../components/docker/setup/buildDockerSetupPayload';
import { DockerSetupFormValues } from '../components/docker/setup/types';
import { DockerRBACFormValues } from '../components/docker/rbac/types';
import { buildKubernetesRBACPayload } from '../components/kubernetes/rbac/buildKubernetesRBACPayload';
import { buildDockerRBACPayload } from '../components/docker/rbac/buildDockerRBACPayload';
import { buildDockerRegistryPayload } from '../components/docker/registry/buildDockerRegistryPayload';
import { buildKubernetesSecurityPayload } from '../components/kubernetes/security/buildKubernetesSecurityPayload';
import { buildKubernetesSetupPayload } from '../components/kubernetes/setup/buildKubernetesSetupPayload';
import { KubernetesSetupFormValues } from '../components/kubernetes/setup/types';
import { buildKubernetesRegistryPayload } from '../components/kubernetes/registry/buildKubernetesRegistryPayload';
import { KubernetesRegistryFormValues } from '../components/kubernetes/registry/types';
import { useUpdatePolicyMutation } from '../queries/useUpdatePolicyMutation';
import { CreatePolicyData } from '../queries/types';
import { PolicyFormByType } from '../components/PolicyFormByType';
import { DockerRegistryFormValues } from '../components/docker/registry/types';
import { KubernetesSecurityFormValues } from '../components/kubernetes/security/types';
import { KubernetesRbacFormValues } from '../components/kubernetes/rbac/types';

interface Props {
  policy: TypedPolicy<unknown>;
}

/**
 * Config tab for editing an existing policy.
 *
 * The confirmation modal is now handled internally by PolicyFormFooter
 * when the user clicks the submit button, so we just need to handle
 * the actual mutation here.
 */
export function ConfigTab({ policy }: Props) {
  const updateMutation = useUpdatePolicyMutation();
  const groupsQuery = useGroups();

  const handleSubmit = useCallback(
    async (values: unknown) => {
      const groups = groupsQuery.data || [];

      const formValues = values as {
        name: string;
        environmentGroupIds: Array<number>;
      };

      // Check if any selected groups have conflicts that need to be overridden
      const needsOverride = hasSelectedGroupsWithConflicts(
        formValues.environmentGroupIds,
        groups,
        policy.Type
      );

      // Build payload based on policy type
      const payloadData = buildPolicyPayload(policy.Type, values);

      await updateMutation.mutateAsync(
        {
          id: policy.Id,
          payload: {
            Name: formValues.name,
            Type: policy.Type,
            EnvironmentType: policy.EnvironmentType,
            EnvironmentGroups: formValues.environmentGroupIds,
            Data: payloadData,
            AllowOverride: needsOverride,
          },
        },
        {
          onSuccess: () => {
            notifySuccess('Success', 'Policy updated successfully');
          },
        }
      );
    },
    [groupsQuery.data, policy, updateMutation]
  );

  if (!policy.Name) {
    return null;
  }

  return (
    <div className="row">
      <div className="col-sm-12">
        <PolicyFormByType
          type={policy.Type}
          mode="edit"
          policy={policy}
          onSubmit={handleSubmit}
        />
      </div>
    </div>
  );
}

/**
 * Build the policy payload based on policy type.
 * Type assertions are safe here as the form values match the policy type.
 */
function buildPolicyPayload(
  type: string,
  formValues: unknown
): CreatePolicyData {
  switch (type) {
    case 'rbac-k8s':
      return buildKubernetesRBACPayload(formValues as KubernetesRbacFormValues);
    case 'security-k8s':
      return buildKubernetesSecurityPayload(
        formValues as KubernetesSecurityFormValues
      );
    case 'setup-k8s':
      return buildKubernetesSetupPayload(
        formValues as KubernetesSetupFormValues
      );
    case 'registry-k8s':
      return buildKubernetesRegistryPayload(
        formValues as KubernetesRegistryFormValues
      );
    case 'rbac-docker':
      return buildDockerRBACPayload(formValues as DockerRBACFormValues);
    case 'security-docker':
      return buildDockerSecurityPayload(formValues as DockerSecurityFormValues);
    case 'setup-docker':
      return buildDockerSetupPayload(formValues as DockerSetupFormValues);
    case 'registry-docker':
      return buildDockerRegistryPayload(formValues as DockerRegistryFormValues);
    default:
      throw new Error(`Unknown policy type: ${type}`);
  }
}
